import _ from "lodash";
import React, { memo } from "react";
import PropTypes from "prop-types";
import ProfileCascade from "UIComponents/ProfileCascade"; // Import the BlockedIcon component
import { Avatar } from "@toddle-edu/ds-web";
import { DisableOutlined, GroupThreeFilled } from "@toddle-edu/ds-icons";
import classes from "./Avatar.scss";
import {
  CHAT_TYPE_ENUM,
  isMessagingRevampV2Enabled,
  isMessagingRevempEnabled,
} from "MessagingV2/modules/Utils";
import classNames from "classnames";
import { getAvatarColor, isIdEqual } from "Utils";
import { getIconObject } from "Course/routes/Settings/modules/SettingsUtils";
import { getUserId } from "modules/CommonSelectors";

const avatarStyles = {
  size: "small",
  shade: "light",
  randomizeColor: true,
};

const avatarStyleV2 = {
  size: "medium",
  shade: "light",
  randomizeColor: true,
  shape: "square",
};

const iconContainerStyle = {
  padding: "4px",
  borderRadius: "6px",
};

const inheritIconStyle = {
  color: "inherit",
};

const getAvatarStyles = ({
  chatType,
  customAvatarStyles,
  color,
  revampV2Enabled,
}) => {
  const shape = isMessagingRevempEnabled() ? "square" : "circle";
  switch (chatType) {
    case CHAT_TYPE_ENUM.CHANNEL_CUSTOM:
    case CHAT_TYPE_ENUM.CHANNEL_PUBLIC:
      return {
        ...(revampV2Enabled ? avatarStyleV2 : avatarStyles),
        ...customAvatarStyles,
        color,
        shape,
      };
    case CHAT_TYPE_ENUM.CHANNEL_FAMILY:
      return {
        ...(revampV2Enabled ? avatarStyleV2 : avatarStyles),
        ...customAvatarStyles,
        color: "violet",
        iconType: "group",
        shape,
        randomizeColor: false,
      };

    case CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED:
      return {
        ...(revampV2Enabled ? avatarStyleV2 : avatarStyles),
        ...customAvatarStyles,
        shape,
        randomizeColor: true,
      };

    default:
      return {
        ...(revampV2Enabled ? avatarStyleV2 : avatarStyles),
        ...customAvatarStyles,
        shape,
      };
  }
};

const getAvatarName = ({ chatType, name }) => {
  switch (chatType) {
    case CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED:
      return _.first(name.replace(/[^a-zA-Z0-9]/g, "")) || " ";

    default:
      return name;
  }
};

const HashtagIcon = ({ style }) => {
  return (
    <div style={style} className={classes.hashtagIcon}>
      #
    </div>
  );
};

const getChannelIcon = ({ icon, url, chatType, hashtagIconStyle = {} }) => {
  const isCustomChatType = _.includes(
    [CHAT_TYPE_ENUM.CHANNEL_CUSTOM, CHAT_TYPE_ENUM.CHANNEL_PUBLIC],
    chatType
  );

  if (isCustomChatType && !url) {
    return <HashtagIcon style={hashtagIconStyle} />;
  }

  if (chatType === CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED && !url && icon) {
    const iconObject = getIconObject({
      size: "x-small",
      containerStyle: iconContainerStyle,
    });
    return _.get(iconObject, [icon, "components"], null);
  }

  if (chatType === CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT && !url) {
    return <GroupThreeFilled style={inheritIconStyle} />;
  }

  return null;
};

const MessagingAvatar = ({
  url,
  icon,
  onlineStatus,
  id,
  name,
  chatType,
  customAvatarStyles,
  isArchived,
  customAvatarContainerClassName,
  hashtagIconStyle,
  isBlocked,
  isDisabled,
}) => {
  const showBlocked = isBlocked && !isIdEqual(id, getUserId());
  const revampV2Enabled = isMessagingRevampV2Enabled();

  const avatarStyleProps = getAvatarStyles({
    chatType,
    customAvatarStyles,
    revampV2Enabled,
    isBlocked: showBlocked,
  });

  const avatarContainerClasses = classNames(
    classes.avatarContainer,
    customAvatarContainerClassName,
    {
      [classes.avatarContainerV2]: revampV2Enabled,
    }
  );

  const profileIcon = getChannelIcon({ icon, url, chatType, hashtagIconStyle });

  return (
    <div className={avatarContainerClasses}>
      {profileIcon ? (
        <Avatar
          {...avatarStyleProps}
          color={getAvatarColor(id)}
          dsVersion={revampV2Enabled ? "2.0" : "1.0"}
          isDisabled={isDisabled}
        >
          {profileIcon}
        </Avatar>
      ) : (
        <ProfileCascade
          items={[
            { id, value: getAvatarName({ chatType, name }), imageUrl: url },
          ]}
          shouldDynamicColor
          showAvatar
          dsVersion={revampV2Enabled ? "2.0" : "1.0"}
          isArchived={isArchived}
          avatarStyles={avatarStyleProps}
          isDisabled={isDisabled}
          {...(isBlocked ? { disabledAvatar: true } : {})}
        />
      )}
      {onlineStatus ? <div className={classes.onlineStatus}>&nbsp;</div> : null}
      {showBlocked ? (
        <div className={classes.blockedIcon}>
          <DisableOutlined size="xxxx-small" />
        </div>
      ) : null}
    </div>
  );
};

MessagingAvatar.propTypes = {
  url: PropTypes.string,
  onlineStatus: PropTypes.bool,
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  isBlocked: PropTypes.bool, // Add isBlocked prop type
};

export default memo(MessagingAvatar);
